import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SystemSidebar = () => {
  const configHistory = [
    {
      id: 1,
      action: "Updated session timeout",
      user: "Admin User",
      timestamp: "03.10.2025 14:30",
      status: "applied"
    },
    {
      id: 2,
      action: "Modified overtime policies",
      user: "HR Manager",
      timestamp: "02.10.2025 16:45",
      status: "pending"
    },
    {
      id: 3,
      action: "Added notification template",
      user: "System Admin",
      timestamp: "01.10.2025 09:15",
      status: "applied"
    }
  ];

  const pendingChanges = [
    {
      id: 1,
      setting: "Shift duration rules",
      requester: "Operations Manager",
      priority: "high"
    },
    {
      id: 2,
      setting: "Email notification settings",
      requester: "HR Team",
      priority: "medium"
    }
  ];

  const systemHealth = [
    {
      service: "Authentication Service",
      status: "healthy",
      uptime: "99.9%",
      lastCheck: "2 min ago"
    },
    {
      service: "Database Connection",
      status: "healthy",
      uptime: "100%",
      lastCheck: "1 min ago"
    },
    {
      service: "Email Service",
      status: "warning",
      uptime: "98.5%",
      lastCheck: "5 min ago"
    },
    {
      service: "Backup Service",
      status: "healthy",
      uptime: "99.8%",
      lastCheck: "30 min ago"
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
        return 'text-success';
      case 'warning':
        return 'text-warning';
      case 'error':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
        return 'CheckCircle';
      case 'warning':
        return 'AlertTriangle';
      case 'error':
        return 'XCircle';
      default:
        return 'Circle';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-error';
      case 'medium':
        return 'text-warning';
      case 'low':
        return 'text-muted-foreground';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div className="w-80 bg-card border-l border-border p-6 overflow-y-auto">
      <div className="space-y-6">
        {/* Configuration History */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Configuration History</h3>
            <Button variant="ghost" size="sm" iconName="History" iconSize={16}>
              View All
            </Button>
          </div>
          <div className="space-y-3">
            {configHistory?.map((item) => (
              <div key={item?.id} className="p-3 bg-muted rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">{item?.action}</p>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    item?.status === 'applied' ?'bg-success/10 text-success' :'bg-warning/10 text-warning'
                  }`}>
                    {item?.status}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{item?.user}</p>
                <p className="text-xs text-muted-foreground">{item?.timestamp}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Pending Changes */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">Pending Approvals</h3>
            <span className="text-xs bg-warning/10 text-warning px-2 py-1 rounded-full">
              {pendingChanges?.length}
            </span>
          </div>
          <div className="space-y-3">
            {pendingChanges?.map((item) => (
              <div key={item?.id} className="p-3 bg-muted rounded-lg">
                <div className="flex items-start justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">{item?.setting}</p>
                  <span className={`text-xs ${getPriorityColor(item?.priority)}`}>
                    {item?.priority}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">{item?.requester}</p>
                <div className="flex space-x-2">
                  <Button variant="outline" size="xs" className="flex-1">
                    Reject
                  </Button>
                  <Button variant="default" size="xs" className="flex-1">
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* System Health */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-foreground">System Health</h3>
            <Button variant="ghost" size="sm" iconName="RefreshCw" iconSize={16}>
              Refresh
            </Button>
          </div>
          <div className="space-y-3">
            {systemHealth?.map((service, index) => (
              <div key={index} className="p-3 bg-muted rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-foreground">{service?.service}</p>
                  <div className="flex items-center space-x-1">
                    <Icon 
                      name={getStatusIcon(service?.status)} 
                      size={14} 
                      className={getStatusColor(service?.status)} 
                    />
                    <span className={`text-xs ${getStatusColor(service?.status)}`}>
                      {service?.status}
                    </span>
                  </div>
                </div>
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Uptime: {service?.uptime}</span>
                  <span>{service?.lastCheck}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="font-medium text-foreground mb-4">Quick Actions</h3>
          <div className="space-y-2">
            <Button 
              variant="outline" 
              fullWidth 
              iconName="Download" 
              iconPosition="left" 
              iconSize={16}
            >
              Export Configuration
            </Button>
            <Button 
              variant="outline" 
              fullWidth 
              iconName="Upload" 
              iconPosition="left" 
              iconSize={16}
            >
              Import Configuration
            </Button>
            <Button 
              variant="outline" 
              fullWidth 
              iconName="RotateCcw" 
              iconPosition="left" 
              iconSize={16}
            >
              Rollback Changes
            </Button>
            <Button 
              variant="destructive" 
              fullWidth 
              iconName="AlertTriangle" 
              iconPosition="left" 
              iconSize={16}
            >
              Emergency Reset
            </Button>
          </div>
        </div>

        {/* System Information */}
        <div className="p-4 bg-muted rounded-lg">
          <h4 className="font-medium text-foreground mb-3">System Information</h4>
          <div className="space-y-2 text-xs text-muted-foreground">
            <div className="flex justify-between">
              <span>Version:</span>
              <span>v2.4.1</span>
            </div>
            <div className="flex justify-between">
              <span>Last Update:</span>
              <span>28.09.2025</span>
            </div>
            <div className="flex justify-between">
              <span>Environment:</span>
              <span>Production</span>
            </div>
            <div className="flex justify-between">
              <span>Database:</span>
              <span>PostgreSQL 14.2</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SystemSidebar;